/**
 * Module 7 — spread analytics (IC / Butterfly / BWIC).
 * Display-only; uses chain mids + BS terminal probabilities (Chaput, Ederington, Goyal & Saretto).
 */
import type { OptionRow } from '../services/gexCalculator';
import type {
  SpreadAnalytics,
  SpreadHeatmapCell,
  SpreadStrategyComparison,
  SpreadStrategyKind,
} from '../types/marketAnalytics';
import { lognormalTerminalProbInRange } from './firstGlanceBsAndMonteCarlo';

const R = 0.052;
const WING_PCTS = [0.01, 0.015, 0.02, 0.025, 0.03, 0.04, 0.05];
const DTE_BUCKETS = [
  { key: '0-4d', min: 0, max: 4 },
  { key: '7-21d', min: 5, max: 21 },
  { key: '30-45d', min: 22, max: 45 },
  { key: '45d+', min: 46, max: 999 },
] as const;

function safeNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function isCall(row: OptionRow): boolean {
  return String(row['Option Type'] ?? row.optionType ?? '').toUpperCase().startsWith('C');
}

function midPrice(row: OptionRow | undefined): number {
  if (!row) return 0;
  const bid = safeNum(row.Bid ?? row.bid);
  const ask = safeNum(row.Ask ?? row.ask);
  if (bid > 0 && ask > 0) return (bid + ask) / 2;
  return safeNum(row.Mid ?? row.Last ?? row.premium);
}

function parseDte(row: OptionRow): number | null {
  const n = safeNum(row.DTE ?? row.dte ?? row['Days To Expiration']);
  if (n >= 0 && Number.isFinite(n)) return n;
  const exp = String(row.Expiration ?? row.expiration ?? row['Expiration Date'] ?? '');
  if (!exp) return null;
  const d = new Date(exp.includes('T') ? exp : `${exp}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.ceil((d.getTime() - Date.now()) / 86400000));
}

function nearestStrike(strikes: number[], target: number): number | null {
  if (!strikes.length) return null;
  return strikes.reduce((best, k) => (Math.abs(k - target) < Math.abs(best - target) ? k : best));
}

type ChainSlice = {
  dte: number;
  calls: Map<number, OptionRow>;
  puts: Map<number, OptionRow>;
  strikes: number[];
};

function buildSlices(rows: OptionRow[]): ChainSlice[] {
  const byExp = new Map<string, OptionRow[]>();
  for (const row of rows) {
    const exp = String(row.Expiration ?? row.expiration ?? row['Expiration Date'] ?? '').trim();
    if (!exp) continue;
    if (!byExp.has(exp)) byExp.set(exp, []);
    byExp.get(exp)!.push(row);
  }

  const slices: ChainSlice[] = [];
  for (const list of Array.from(byExp.values())) {
    const dte = parseDte(list[0]);
    if (dte == null) continue;
    const calls = new Map<number, OptionRow>();
    const puts = new Map<number, OptionRow>();
    for (const row of list) {
      const k = safeNum(row.Strike ?? row.strike);
      if (k <= 0) continue;
      if (isCall(row)) calls.set(k, row);
      else puts.set(k, row);
    }
    const strikes = Array.from(
      new Set([...Array.from(calls.keys()), ...Array.from(puts.keys())]),
    ).sort((a, b) => a - b);
    if (strikes.length >= 4) slices.push({ dte, calls, puts, strikes });
  }
  return slices.sort((a, b) => a.dte - b.dte);
}

function pickSliceForBucket(slices: ChainSlice[], bucket: (typeof DTE_BUCKETS)[number]): ChainSlice | null {
  const candidates = slices.filter((s) => s.dte >= bucket.min && s.dte <= bucket.max);
  if (!candidates.length) return null;
  return candidates.reduce((best, s) => (s.strikes.length > best.strikes.length ? s : best));
}

function legCredit(short: number, long: number, book: Map<number, OptionRow>): number {
  return Math.max(0, midPrice(book.get(short)) - midPrice(book.get(long)));
}

function buildIronCondor(
  slice: ChainSlice,
  spot: number,
  wingPct: number,
): { credit: number; width: number; beLow: number | null; beHigh: number | null } | null {
  const wing = spot * wingPct;
  const shortCall = nearestStrike(slice.strikes.filter((k) => k > spot), spot + wing);
  const shortPut = nearestStrike(slice.strikes.filter((k) => k < spot), spot - wing);
  if (shortCall == null || shortPut == null) return null;
  const longCall = nearestStrike(slice.strikes.filter((k) => k > shortCall), shortCall + wing);
  const longPut = nearestStrike(slice.strikes.filter((k) => k < shortPut), shortPut - wing);
  if (longCall == null || longPut == null) return null;

  const credit = legCredit(shortCall, longCall, slice.calls) + legCredit(shortPut, longPut, slice.puts);
  const width = Math.abs(longCall - shortCall) + Math.abs(shortPut - longPut);
  if (width <= 0) return null;

  return { credit, width, beLow: shortPut - credit, beHigh: shortCall + credit };
}

function buildIronButterfly(
  slice: ChainSlice,
  spot: number,
  wingPct: number,
): { credit: number; width: number; beLow: number | null; beHigh: number | null } | null {
  const atm = nearestStrike(slice.strikes, spot);
  if (atm == null) return null;
  const wing = spot * wingPct;
  const longCall = nearestStrike(slice.strikes.filter((k) => k > atm), atm + wing);
  const longPut = nearestStrike(slice.strikes.filter((k) => k < atm), atm - wing);
  if (longCall == null || longPut == null) return null;

  const credit =
    midPrice(slice.calls.get(atm)) +
    midPrice(slice.puts.get(atm)) -
    midPrice(slice.calls.get(longCall)) -
    midPrice(slice.puts.get(longPut));
  if (credit <= 0) return null;
  const width = Math.abs(longCall - atm) + Math.abs(atm - longPut);
  return { credit, width, beLow: atm - credit, beHigh: atm + credit };
}

function buildBwic(
  slice: ChainSlice,
  spot: number,
  wingPct: number,
): { credit: number; width: number; beLow: number | null; beHigh: number | null } | null {
  const putWing = spot * wingPct * 1.22;
  const callWing = spot * wingPct * 0.88;
  const shortCall = nearestStrike(slice.strikes.filter((k) => k > spot), spot + callWing);
  const shortPut = nearestStrike(slice.strikes.filter((k) => k < spot), spot - putWing);
  if (shortCall == null || shortPut == null) return null;
  const longCall = nearestStrike(slice.strikes.filter((k) => k > shortCall), shortCall + callWing);
  const longPut = nearestStrike(slice.strikes.filter((k) => k < shortPut), shortPut - putWing);
  if (longCall == null || longPut == null) return null;

  const credit = legCredit(shortCall, longCall, slice.calls) + legCredit(shortPut, longPut, slice.puts);
  const width = Math.abs(longCall - shortCall) + Math.abs(shortPut - longPut);
  if (width <= 0 || credit <= 0) return null;
  return { credit, width, beLow: shortPut - credit, beHigh: shortCall + credit };
}

function stayInsideProb(
  spot: number,
  ivPct: number,
  dte: number,
  low: number | null,
  high: number | null,
): number | null {
  if (low == null || high == null || ivPct <= 0) return null;
  const T = Math.max(dte / 365, 1 / 365);
  const sigma = ivPct / 100;
  return lognormalTerminalProbInRange(spot, low, high, T, R, sigma);
}

function compareStrategy(
  kind: SpreadStrategyKind,
  label_en: string,
  label_ar: string,
  pack: { credit: number; width: number; beLow: number | null; beHigh: number | null } | null,
  spot: number,
  ivPct: number,
  dte: number,
): SpreadStrategyComparison | null {
  if (!pack || pack.width <= 0) return null;
  const cwr = pack.credit / pack.width;
  const stay = stayInsideProb(spot, ivPct, dte, pack.beLow, pack.beHigh);
  return {
    kind,
    label_en,
    label_ar,
    credit: pack.credit,
    total_width: pack.width,
    cwr,
    breakeven_low: pack.beLow,
    breakeven_high: pack.beHigh,
    stay_inside_prob: stay,
    display_score: stay != null ? cwr * stay : cwr,
  };
}

const EMPTY_SPREAD: SpreadAnalytics = {
  optimal_wing_pct: null,
  optimal_dte_bucket: null,
  optimal_cwr: null,
  heatmap: [],
  comparisons: [],
};

export function buildSpreadAnalytics(
  rows: OptionRow[],
  spot: number,
  atmIvPct: number | null,
): SpreadAnalytics {
  if (!rows.length || spot <= 0) return EMPTY_SPREAD;

  const slices = buildSlices(rows);
  if (!slices.length) return EMPTY_SPREAD;

  const iv = atmIvPct != null && atmIvPct > 0 ? atmIvPct : 20;
  const heatmap: SpreadHeatmapCell[] = [];
  let bestCell: SpreadHeatmapCell | null = null;

  for (const bucket of DTE_BUCKETS) {
    const slice = pickSliceForBucket(slices, bucket);
    if (!slice) continue;
    for (const wingPct of WING_PCTS) {
      const ic = buildIronCondor(slice, spot, wingPct);
      if (!ic) continue;
      const cell: SpreadHeatmapCell = {
        wing_pct: wingPct,
        dte_bucket: bucket.key,
        cwr: ic.credit / ic.width,
        credit: ic.credit,
        wing_width: spot * wingPct,
      };
      heatmap.push(cell);
      if (!bestCell || cell.cwr > bestCell.cwr) bestCell = cell;
    }
  }

  const refBucket = bestCell
    ? DTE_BUCKETS.find((b) => b.key === bestCell!.dte_bucket)
    : undefined;
  const refSlice = refBucket ? pickSliceForBucket(slices, refBucket) : slices[0];
  const refWing = bestCell?.wing_pct ?? 0.02;
  const refDte = refSlice?.dte ?? 30;

  const comparisons: SpreadStrategyComparison[] = [];
  if (refSlice) {
    const ic = compareStrategy(
      'iron_condor',
      'Iron Condor',
      'حديد كوندور',
      buildIronCondor(refSlice, spot, refWing),
      spot,
      iv,
      refDte,
    );
    const ibf = compareStrategy(
      'iron_butterfly',
      'Iron Butterfly',
      'فراشة حديد',
      buildIronButterfly(refSlice, spot, refWing),
      spot,
      iv,
      refDte,
    );
    const bwic = compareStrategy(
      'bwic',
      'BWIC',
      'BWIC',
      buildBwic(refSlice, spot, refWing),
      spot,
      iv,
      refDte,
    );
    if (ic) comparisons.push(ic);
    if (ibf) comparisons.push(ibf);
    if (bwic) comparisons.push(bwic);
    comparisons.sort((a, b) => b.display_score - a.display_score);
  }

  return {
    optimal_wing_pct: bestCell?.wing_pct ?? null,
    optimal_dte_bucket: bestCell?.dte_bucket ?? null,
    optimal_cwr: bestCell?.cwr ?? null,
    heatmap,
    comparisons,
  };
}
